import { Logger } from '@/services/logger/logger';

export interface PromiseCacherExecOptions<TResult> {
  name: string;
  forceRefresh: boolean;
  inFlightOnly: boolean;
  workFn: () => Promise<TResult>;
}

export interface PromiseCacher {

  /** If inFlightOnly is set to true for a specific API request, it should always be set to true
   *  for the duration of the app. There is a scenario when inFlightOnly has been set to false for a request, then
   *  set to true for a second request, the cached result will be returned instead of making a new request
   */
  exec<T>(name: string, forceRefresh: boolean, workFn: () => Promise<T>, inFlightOnly?: boolean): Promise<T>;
  exec<T>(options: PromiseCacherExecOptions<T>): Promise<T>;
}

/*
  Only call a request once. If another call is already in flight, just wait for that one
*/
export class AppPromiseCacher implements PromiseCacher {
  private inFlightPromises: { [name: string]: Promise<any> | undefined } = {};

  constructor(private logger: Logger) {}

  async exec<T>(nameOrOptions: string | PromiseCacherExecOptions<T>, forceRefresh?: boolean, workFn?: () => Promise<T>, inFlightOnly: boolean = false): Promise<T> {
    let name = '';
    if (typeof nameOrOptions === 'string') {
      name = nameOrOptions;
    } else {
      name = nameOrOptions.name;
      forceRefresh = nameOrOptions.forceRefresh;
      workFn = nameOrOptions.workFn;
      inFlightOnly = nameOrOptions.inFlightOnly;
    }

    if (!forceRefresh) {
      const existingPromise = this.inFlightPromises[name];
      if (!!existingPromise) {
        this.logger.debug(`Using existing promise for ${name}`);

        return existingPromise;
      }
    }

    this.logger.debug(`Executing new promise for ${name}`);

    // Get the promise to exec and add to map of promises
    const promise = workFn!();
    this.inFlightPromises[name] = promise;

    const result = await promise;

    // If we only care about caching the promise for the duration of the request, remove it after completion
    if (inFlightOnly) {
      delete this.inFlightPromises[name];
    }

    return result;
  }

  cache<T>(name: string, promise: Promise<T>) {
    this.inFlightPromises[name] = promise;
  }
}

export function awaitCondition(condition: () => boolean): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const waiting = setInterval(()=>{
      if(condition()){
        clearTimeout(waiting); // clearInterval() didnt work here
        resolve(true);
      }
    }, 250);
  });
}